import { useQuery } from '@tanstack/react-query';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Loader2, FileText } from 'lucide-react';
import { getBlogStats } from '@/lib/analytics-api';

interface Props {
  projectId: string;
  from: string;
  to: string;
  customerId?: string;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}
function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function BlogPanel({ projectId, from, to, customerId }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['blog', projectId, from, to, customerId],
    queryFn: () => getBlogStats({ from, to, projectId }, customerId),
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-4 text-sm text-red-600">
        Blog-Daten konnten nicht geladen werden: {(error as any).message}
      </div>
    );
  }
  if (!data?.connected) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
        Für diese Landing Page ist kein PUBLIVO-Blog verbunden.
      </div>
    );
  }
  if (data.available === false) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
        PUBLIVO ist serverseitig noch nicht konfiguriert.
      </div>
    );
  }

  const t = data.totals!;
  const timeline = (data.timeline || []).map((w) => ({ ...w }));
  const notLive = !!data.blog_status && data.blog_status !== 'live';

  return (
    <div className="space-y-4">
      {/* Blog noch nicht live: Nullzahlen sind dann KEIN Befund, sondern Folge des fehlenden DNS-Eintrags */}
      {notLive && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <p className="font-semibold">⚠ Blog ist noch nicht live — DNS-Eintrag fehlt</p>
          <p className="mt-1 text-amber-700">
            Der Blog {data.blog_domain ? <>(<span className="font-mono">{data.blog_domain}</span>)</> : null} wird in PUBLIVO bereits geführt,
            ist aber erst öffentlich erreichbar, sobald der DNS-Eintrag auf der Kundendomain gesetzt ist
            (Status: {data.blog_status}). Bis dahin sind 0 Klicks/Impressionen normal und keine Aussage über die Blog-Leistung.
          </p>
        </div>
      )}

      {/* Blog-Kennzahlen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <BlogCard label="Blog-Artikel" value={`${fmt(t.articles_published)}`} hint={`${t.articles_total} gesamt (inkl. zurückgezogene)`} />
        <BlogCard label="Blog-Klicks (Google)" value={fmt(t.blog_clicks)} hint="Klicks auf Blog-Seiten aus der Google-Suche" />
        <BlogCard label="Blog-Impressionen" value={fmt(t.blog_impressions)} hint="Wie oft Blog-Seiten in Google erschienen" />
        <BlogCard
          label="Blog-Anteil"
          value={`${(t.blog_share_clicks * 100).toFixed(0)} %`}
          hint="Anteil des Blogs an allen Google-Klicks der Domain"
        />
      </div>

      {/* Input/Output-Zeitachse */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-1">Input vs. Output</h2>
        <p className="text-xs text-gray-500 mb-4">
          Veröffentlichte Artikel (Balken) vs. Blog-Klicks aus Google (Linie) – so wird sichtbar, was der Blog bewirkt.
        </p>
        {timeline.length === 0 ? (
          <div className="h-52 flex items-center justify-center text-sm text-gray-400">
            Noch keine Blog-Daten im Zeitraum (Google-Daten hinken 1–3 Tage nach).
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={timeline} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="articles_published" name="Artikel veröffentlicht" fill="#c4b5fd" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="blog_clicks" name="Blog-Klicks" stroke="#7c3aed" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Pro-Artikel-Leistung */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900">Artikel-Leistung</h2>
        </div>
        {(data.articles || []).length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Noch keine Artikel vorhanden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-2 font-medium">Artikel</th>
                  <th className="px-4 py-2 font-medium">Säule</th>
                  <th className="px-4 py-2 font-medium">Veröffentlicht</th>
                  <th className="px-4 py-2 font-medium text-right">Google-Klicks</th>
                  <th className="px-4 py-2 font-medium text-right">Impress.</th>
                  <th className="px-4 py-2 font-medium text-right">Ø Pos.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.articles || []).map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-800 max-w-xs truncate" title={a.title}>
                      {a.title}
                      {a.status !== 'published' && <span className="ml-1 text-xs text-gray-400">(zurückgezogen)</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{a.pillar_name || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">{formatDate(a.published_at)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{fmt(a.clicks)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">{fmt(a.impressions)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">{a.position ? a.position.toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BlogCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4" title={hint}>
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
